import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';
import { addToast, Button } from '@octano/global-ui';
import { useForm, FormProvider } from 'react-hook-form';

import { setInterviewRequired } from './api';
import StudyPlanVersionOffersSection from './parts/StudyPlanVersionOffersSection';
import PeriodSelectorSection from './parts/PeriodSelectorSection';
import { usePeriods } from './parts/Loaders/PeriodsLoader';
import { useStudyPlanOffers } from './parts/Loaders/StudyPlanOffersLoader';

export default function EnrollmentAndTuition() {
  const { t } = useTranslation('configuration/enrollment-and-tuition');
  const methods = useForm({
    defaultValues: {
      selectedPeriod: null as { label: string; value: number } | null,
    },
  });

  const [saving, setSaving] = useState(false);
  const [localChanges, setLocalChanges] = useState<Map<number, boolean>>(
    new Map(),
  );

  const periodsLoader = usePeriods();
  const studyPlanOffersLoader = useStudyPlanOffers();

  const periods = periodsLoader.data || [];
  const studyPlanVersionOffers = useMemo(
    () => studyPlanOffersLoader.data || [],
    [studyPlanOffersLoader.data],
  );
  const isLoading = periodsLoader.loading || studyPlanOffersLoader.loading;

  const studyPlanVersionOffersWithLocalChanges = useMemo(() => {
    return studyPlanVersionOffers.map((offer) => {
      const hasLocalChange = localChanges.has(offer.id);
      const isInterviewRequired = hasLocalChange
        ? localChanges.get(offer.id) ?? offer.isInterviewRequired
        : offer.isInterviewRequired;

      return {
        ...offer,
        isInterviewRequired,
      };
    });
  }, [studyPlanVersionOffers, localChanges]);

  const handlePeriodChange = useCallback(
    (data: { label: string; value: number }) => {
      setLocalChanges(new Map());
      methods.setValue('selectedPeriod', {
        label: data.label,
        value: data.value,
      });
      studyPlanOffersLoader.updateQuery({ academicOfferId: data.value });
    },
    [studyPlanOffersLoader, methods],
  );

  const handleInterviewRequiredChange = useCallback(
    (offerId: number, isRequired: boolean) => {
      setLocalChanges((prevChanges) => {
        const newChanges = new Map(prevChanges);
        const originalOffer = studyPlanVersionOffers.find(
          (offer) => offer.id === offerId,
        );

        if (originalOffer && originalOffer.isInterviewRequired === isRequired) {
          newChanges.delete(offerId);
        } else {
          newChanges.set(offerId, isRequired);
        }

        return newChanges;
      });
    },
    [studyPlanVersionOffers],
  );

  const handleSaveChanges = useCallback(async () => {
    if (localChanges.size === 0) return;

    setSaving(true);
    try {
      const updates = Array.from(localChanges.entries()).map(
        ([studyPlanVersionOfferId, isInterviewRequired]) => ({
          studyPlanVersionOfferId,
          isInterviewRequired,
        }),
      );

      const { error } = await setInterviewRequired({ updates });

      if (error) {
        addToast({
          text: t('error_message'),
          color: 'danger',
          icon: 'error',
        });
      } else {
        addToast({
          text: t('successMessage'),
          color: 'success',
          icon: 'success',
        });
        setLocalChanges(new Map());
        studyPlanOffersLoader.refresh();
      }
    } catch (err) {
      addToast({
        text: t('error_message'),
        color: 'danger',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [localChanges, studyPlanOffersLoader, t]);

  const selectedPeriod = methods.watch('selectedPeriod');

  return (
    <FormProvider {...methods}>
      <Card className="p-4">
        <Row>
          <Col sm={12}>
            <p className="fs-14 text-medium mb-3">{t('description')}</p>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <PeriodSelectorSection
              periods={periods}
              onPeriodChange={handlePeriodChange}
              disabled={isLoading}
            />
          </Col>
          {selectedPeriod && (
            <>
              <Col sm={12}>
                <StudyPlanVersionOffersSection
                  studyPlanVersionOffers={
                    studyPlanVersionOffersWithLocalChanges
                  }
                  isLoading={isLoading}
                  disabled={saving || isLoading}
                  onInterviewRequiredChange={handleInterviewRequiredChange}
                />
              </Col>
              <Col sm={12} className="mt-4 d-flex justify-content-end">
                <Button
                  onClick={handleSaveChanges}
                  disabled={saving || isLoading}
                  size="md"
                  loading={saving}
                  text={t('saveButton')}
                />
              </Col>
            </>
          )}
        </Row>
      </Card>
    </FormProvider>
  );
}
